/**
 * Turning a hand code into cards someone can look at.
 *
 * The charts speak in the 169 codes — "AA", "AKs", "T7o" — because that is the
 * resolution the game actually has preflop. A person being dealt a hand needs two
 * concrete cards, so this picks a representative pair. Which suits are chosen changes
 * nothing about the strategy; it is presentation, and it is kept well away from
 * anything that computes.
 */

export const RANKS = "23456789TJQKA";
const SUIT_CHAR = ["♣", "♦", "♥", "♠"];
const SUIT_NAME = ["clubs", "diamonds", "hearts", "spades"];

export interface DealtCard { rank: string; suit: number }

/** Combos of a hand code, weighted correctly: 6 for a pair, 4 suited, 12 offsuit. */
export function comboWeight(code: string): number {
  if (code.length === 2) return 6;
  return code[2] === "s" ? 4 : 12;
}

/** Two concrete cards for a hand code, with suits chosen at random among the valid ones. */
export function dealCards(code: string, rnd: () => number): DealtCard[] {
  const a = code[0]!, b = code[1]!;
  if (code.length === 2) {
    // A pair: two different suits.
    const s1 = Math.floor(rnd() * 4);
    let s2 = Math.floor(rnd() * 3);
    if (s2 >= s1) s2++;
    return [{ rank: a, suit: s1 }, { rank: b, suit: s2 }];
  }
  if (code[2] === "s") {
    const s = Math.floor(rnd() * 4);
    return [{ rank: a, suit: s }, { rank: b, suit: s }];
  }
  const s1 = Math.floor(rnd() * 4);
  let s2 = Math.floor(rnd() * 3);
  if (s2 >= s1) s2++;
  return [{ rank: a, suit: s1 }, { rank: b, suit: s2 }];
}

export const suitChar = (s: number) => SUIT_CHAR[s]!;
export const suitName = (s: number) => SUIT_NAME[s]!;
/** Diamonds and hearts read red, the way a deck does. */
export const suitIsRed = (s: number) => s === 1 || s === 2;

/**
 * Pick a hand at random, weighted by how often it is actually dealt.
 *
 * Uniform over the 169 codes would deal AA as often as AKo, which is four times too
 * many aces and would quietly teach the wrong frequencies. The weights come from the
 * chart itself, so the trainer deals from the same distribution the solver assumed.
 */
export function weightedHand(strategy: Record<string, { weight?: number }>, rnd: () => number): string {
  const codes = Object.keys(strategy);
  let total = 0;
  for (const c of codes) total += strategy[c]!.weight ?? comboWeight(c);
  let r = rnd() * total;
  for (const c of codes) {
    r -= strategy[c]!.weight ?? comboWeight(c);
    if (r <= 0) return c;
  }
  return codes[codes.length - 1]!;
}

/**
 * How a player would say where they are sitting, preposition included.
 *
 * The preposition is part of the name, not something a template can prepend: it is
 * "ON the button" and "UNDER the gun" but "IN the cutoff". Returning a bare noun and
 * gluing "in the" in front of it produced "You are in the button", which is the kind
 * of small wrongness that tells a poker player immediately that whoever built this
 * does not play.
 */
const SEAT_PHRASE: Record<string, string> = {
  UTG: "under the gun",
  UTG1: "in UTG+1",
  UTG2: "in UTG+2",
  MP: "in middle position",
  MP1: "in MP+1",
  CO: "in the cutoff",
  BTN: "on the button",
  SB: "in the small blind",
  BB: "in the big blind",
};

export const seatPhrase = (actor: string): string => SEAT_PHRASE[actor] ?? `in ${actor}`;
